import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="FINBRO Logo" className="w-10 h-10 rounded-lg object-cover" />
              <span className="text-2xl font-bold text-gray-900">FINBRO</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" /> Înapoi
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="prose prose-lg max-w-none">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Termeni și Condiții de Utilizare a Platformei FINBRO
          </h1>

          <p className="text-gray-600 mb-8">
            Acești Termeni și Condiții stabilesc regulile generale care guvernează utilizarea platformei FINBRO 
            (denumită în continuare "Platforma" sau "Site-ul") de către dumneavoastră ca Utilizator sau ca Intermediar de Credite. 
            Prin accesarea și utilizarea Platformei, confirmați că ați citit, înțeles și acceptat acești termeni.
          </p>

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8">
            <p className="text-blue-800 font-medium">
              FINBRO nu este o instituție financiară sau bancă. Platforma facilitează conexiunea dintre 
              intermediarii de credite și clienții acestora, oferind instrumente digitale pentru 
              gestionarea procesului de intermediere.
            </p>
          </div>

          {/* Secțiunea 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 1. Cine suntem?</h2>
            <p className="text-gray-700 mb-4">
              Platforma FINBRO (finbro.ro) este administrată de:
            </p>
            <div className="bg-gray-100 p-4 rounded-lg mb-4">
              <p className="font-semibold">CHANGEMATRIC SRL</p>
              <p>CUI: 41017573</p>
              <p>Nr. Reg. Com.: J40/5503/2019</p>
              <p>Sediul social: București, România</p>
            </div>
            <p className="text-gray-700">
              Denumită în continuare "Societatea", "FINBRO" sau "noi".
            </p>
          </section>

          {/* Secțiunea 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 2. Scopul Platformei</h2>
            <p className="text-gray-700 mb-4">
              FINBRO este o platformă SaaS (Software as a Service) destinată intermediarilor de credite 
              din România, oferind următoarele funcționalități:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Generarea de formulare online pentru colectarea datelor de la clienți</li>
              <li>Calculator de credit pentru compararea ofertelor bancare</li>
              <li>Generarea automată de documente conforme cu OUG 52 și GDPR</li>
              <li>Gestionarea relației cu clienții și urmărirea cererilor de credit</li>
              <li>Instrumente de analiză și raportare pentru activitatea de intermediere</li>
            </ul>
          </section>

          {/* Secțiunea 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 3. Tipuri de Utilizatori</h2>
            
            <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Intermediari de Credite</h3>
            <p className="text-gray-700 mb-4">
              Persoane juridice sau fizice autorizate să desfășoare activități de intermediere de credite 
              conform legislației în vigoare. Intermediarii de credite pot crea conturi pe platformă, gestiona clienți 
              și utiliza toate funcționalitățile disponibile conform planului de abonament ales.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.2 Clienți Finali</h3>
            <p className="text-gray-700 mb-4">
              Persoane fizice care accesează formulare online la invitația unui intermediar de credite pentru a solicita 
              servicii de intermediere. Clienții finali nu au conturi pe platformă și 
              interacționează doar prin linkurile primite de la intermediari.
            </p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.3 Administratori</h3>
            <p className="text-gray-700">
              Utilizatori cu drepturi extinse de gestionare a platformei, inclusiv administrarea 
              bazei de date cu produse bancare și validarea conturilor de intermediari de credite.
            </p>
          </section>

          {/* Secțiunea 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 4. Înregistrarea și Contul</h2>
            <p className="text-gray-700 mb-4">
              Pentru a utiliza Platforma ca intermediar de credite, trebuie să vă creați un cont furnizând informații 
              corecte și complete despre dumneavoastră și compania pe care o reprezentați.
            </p>
            <p className="text-gray-700 mb-4">
              Sunteți responsabil pentru:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Păstrarea confidențialității datelor de autentificare</li>
              <li>Toate activitățile care au loc în contul dumneavoastră</li>
              <li>Notificarea imediată a oricărei utilizări neautorizate a contului</li>
              <li>Actualizarea informațiilor din profil atunci când acestea se modifică</li>
            </ul>
          </section>

          {/* Secțiunea 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 5. Planuri de Abonament</h2>
            <p className="text-gray-700 mb-4">
              FINBRO oferă următoarele planuri de abonament:
            </p>
            
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-100 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900">BASIC (Gratuit)</h4>
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>• Formular client OUG 52 cu link</li>
                  <li>• Trimitere cereri pe email</li>
                  <li>• Vizualizare oferte bancare</li>
                  <li>• Calculator rate + scadențar</li>
                  <li>• Generare ofertă PDF</li>
                  <li>• Branding logo pe documente</li>
                  <li>• Istoric cereri</li>
                  <li>• Conform OUG 52 & OUG 50</li>
                </ul>
              </div>
              <div className="bg-blue-100 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900">PROFESIONAL</h4>
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>• Tot din BASIC</li>
                  <li>• Calculator taxe notariale</li>
                  <li>• Calculator salariu</li>
                  <li>• Calculator grad de îndatorare</li>
                  <li>• Fișe produse bancare actualizate</li>
                </ul>
              </div>
              <div className="bg-purple-100 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900">PREMIUM</h4>
                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                  <li>• Tot din PROFESIONAL</li>
                  <li>• Agent AI bancar</li>
                  <li>• Acces funcționalități noi</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Secțiunea 6 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 6. Obligațiile Utilizatorilor</h2>
            <p className="text-gray-700 mb-4">
              Prin utilizarea Platformei, vă angajați să:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Respectați toate legile și reglementările aplicabile activității de intermediere de credite</li>
              <li>Furnizați informații corecte și actualizate</li>
              <li>Nu utilizați Platforma în scopuri ilegale sau neautorizate</li>
              <li>Nu încercați să accesați neautorizat sistemele sau datele altor utilizatori</li>
              <li>Nu distribuiți conținut dăunător, ofensator sau care încalcă drepturile altora</li>
              <li>Respectați confidențialitatea datelor clienților conform GDPR</li>
            </ul>
          </section>

          {/* Secțiunea 7 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 7. Limitarea Răspunderii</h2>
            <p className="text-gray-700 mb-4">
              FINBRO:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Nu oferă servicii de consiliere financiară sau de creditare</li>
              <li>Nu garantează aprobarea creditelor pentru clienții finali</li>
              <li>Nu este parte în relația contractuală dintre broker și client sau dintre client și bancă</li>
              <li>Nu răspunde pentru deciziile de creditare ale instituțiilor financiare</li>
              <li>Nu garantează disponibilitatea neîntreruptă a serviciilor</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Platforma este furnizată "ca atare" (as is). Nu oferim garanții explicite sau implicite 
              privind adecvarea pentru un anumit scop.
            </p>
          </section>

          {/* Secțiunea 8 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 8. Proprietate Intelectuală</h2>
            <p className="text-gray-700 mb-4">
              Toate drepturile de proprietate intelectuală asupra Platformei, inclusiv dar fără a se limita la 
              codul sursă, design, logo-uri, texte și grafică, aparțin CHANGEMATRIC SRL.
            </p>
            <p className="text-gray-700">
              Utilizatorii nu au dreptul să copieze, modifice, distribuie sau să creeze lucrări derivate 
              din conținutul Platformei fără acordul scris prealabil.
            </p>
          </section>

          {/* Secțiunea 9 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 9. Protecția Datelor</h2>
            <p className="text-gray-700 mb-4">
              Procesarea datelor cu caracter personal se realizează în conformitate cu Regulamentul 
              General privind Protecția Datelor (GDPR) și legislația română în vigoare.
            </p>
            <p className="text-gray-700">
              Pentru detalii complete privind modul în care colectăm, utilizăm și protejăm datele 
              dumneavoastră, vă rugăm să consultați{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Politica de Confidențialitate
              </Link>.
            </p>
          </section>

          {/* Secțiunea 10 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 10. Modificări ale Termenilor</h2>
            <p className="text-gray-700">
              Ne rezervăm dreptul de a modifica acești Termeni și Condiții în orice moment. 
              Modificările vor fi publicate pe această pagină și vor intra în vigoare imediat 
              după publicare. Continuarea utilizării Platformei după publicarea modificărilor 
              constituie acceptarea acestora.
            </p>
          </section>

          {/* Secțiunea 11 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 11. Încetarea Utilizării</h2>
            <p className="text-gray-700 mb-4">
              Puteți înceta utilizarea Platformei în orice moment prin ștergerea contului.
            </p>
            <p className="text-gray-700">
              Ne rezervăm dreptul de a suspenda sau închide contul dumneavoastră în cazul 
              încălcării acestor Termeni și Condiții sau a legislației aplicabile.
            </p>
          </section>

          {/* Secțiunea 12 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 12. Soluționarea Litigiilor</h2>
            <p className="text-gray-700 mb-4">
              Acești Termeni și Condiții sunt guvernați de legea română.
            </p>
            <p className="text-gray-700 mb-4">
              În cazul oricărei dispute, părțile vor încerca mai întâi soluționarea amiabilă. 
              Dacă aceasta nu este posibilă, litigiul va fi supus instanțelor competente din București.
            </p>
            <p className="text-gray-700">
              Pentru soluționarea alternativă a litigiilor, puteți contacta:<br />
              <strong>Autoritatea Națională pentru Protecția Consumatorilor (ANPC)</strong><br />
              Website: <a href="http://www.anpc.gov.ro" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">www.anpc.gov.ro</a>
            </p>
          </section>

          {/* Secțiunea 13 */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Secțiunea 13. Contact</h2>
            <p className="text-gray-700">
              Pentru orice întrebări sau sesizări legate de acești Termeni și Condiții, 
              ne puteți contacta la:
            </p>
            <div className="bg-gray-100 p-4 rounded-lg mt-4">
              <p><strong>CHANGEMATRIC SRL</strong></p>
              <p>Email: contact@finbro.ro</p>
              <p>CUI: 41017573</p>
            </div>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-gray-500 text-sm">
              Ultima actualizare: Ianuarie 2025
            </p>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-400">
            © 2025 FINBRO by CHANGEMATRIC SRL. Toate drepturile rezervate.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
